import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import * as XLSX from 'xlsx';
import './InvoiceModule.css';
import Swal from 'sweetalert2';
import API_ENDPOINTS from '../../../config/apiEndpoints';
import {
    Plus, Download, Filter, Search, MoreVertical,
    Edit, Trash2, Copy, Send, X, Calendar, Table,
    Phone, Eye, Edit3, Banknote, XCircle, FileCheck,
    ArrowLeft, ChevronDown, ChevronUp, User, GripVertical,
    LayoutGrid, List, Settings, FileText, Mail, MessageSquare, Printer, DownloadCloud,
    Share2, MessageCircle, History, Share, Upload
} from 'lucide-react';
import { Icon } from '@iconify/react';
import Loader from '../../../components/Loader/Loader';
import InvoiceFilterationAction from './InvoiceFilterationAction';
import InvoiceTemplatePanel from './InvoiceTemplatePanel';
import InvoiceAdvancedFilter from './InvoiceAdvancedFilter';
import DynamicCalendar from '../../../components/DynamicCalendar/DynamicCalendar';
import { LeadDetails as CommonLeadDetails } from '../../../components/common/LeadDetails/LeadDetails';
import SubHeader from '../../../components/common/SubHeader/SubHeader';
import LeadActionOperation from '../../../common/LeadActionOperation/LeadActionOperation';
import ScoreCard from '../../../components/common/ScoreCard/ScoreCard';
import usePermissions from '../../Master_Settings/usePermissions';

// Premium Components & MUI
import PremiumModuleTable from '../../Task/Components/common/PremiumModuleTable';
import PremiumModuleCards from '../../Task/Components/common/PremiumModuleCards';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Badge from '@mui/material/Badge';
import Link from '@mui/material/Link';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';

const API_BASE = (process.env.REACT_APP_WCF_API_BASE_URL || 'https://wcfkit19.azurewebsites.net').replace(/\/$/, '');
const NEW_BASE_URL = (process.env.REACT_APP_SERVICES_AZURE_BASEURL || '').replace(/\/$/, '');


const InvoiceModule = () => {
    const { permissions } = usePermissions('PAG10223');
    const crmOperationPerms = useMemo(() => {
        if (!permissions || !Array.isArray(permissions)) return null;
        return permissions.find(p => p.ModuleCode?.includes('PAG10223')) || {};
    }, [permissions]);

    const [searchParams, setSearchParams] = useSearchParams();

    // Handle incoming actions from other modules (e.g., EntityEdit)
    useEffect(() => {
        const action = searchParams.get('action');
        if (action === 'add' && actionRef.current) {
            const leadId = searchParams.get('leadId');
            const timer = setTimeout(() => {
                actionRef.current.handleAddInvoice(leadId);
                // Clear search params to avoid re-triggering on manual refresh
                const newParams = new URLSearchParams(searchParams);
                newParams.delete('action');
                newParams.delete('leadId');
                setSearchParams(newParams, { replace: true });
            }, 600);
            return () => clearTimeout(timer);
        }
    }, [searchParams, setSearchParams]);

    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [hasFetched, setHasFetched] = useState(false);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [totalRecords, setTotalRecords] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(25);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [activeDropdownId, setActiveDropdownId] = useState(null);
    const [viewMode, setViewMode] = useState('table'); // 'table', 'grid', or 'calendar'
    const [statusFilters, setStatusFilters] = useState([]);
    const [approvalRemark, setApprovalRemark] = useState('');

    // --- Robust Status Mapping Helper ---
    const getStandardStatus = (rawStatus) => {
        const s = String(rawStatus || '').toLowerCase();
        if (s.includes('paid') || s.includes('approved')) return 'Paid/Approved';
        if (s.includes('draft') || s.includes('edit')) return 'Draft';
        if (s.includes('sent') || s.includes('pend')) return 'Sent For Approval';
        if (s.includes('canc') || s.includes('rej') || s.includes('deleted')) return 'Cancelled';
        return 'Draft'; // Default
    };

    const statsCounts = useMemo(() => {
        const counts = { approved: 0, draft: 0, pending: 0, cancelled: 0 };
        invoices.forEach(inv => {
            const status = getStandardStatus(inv.Status);
            if (status === 'Paid/Approved') counts.approved++;
            else if (status === 'Draft') counts.draft++;
            else if (status === 'Sent For Approval') counts.pending++;
            else if (status === 'Cancelled') counts.cancelled++;
        });
        return counts;
    }, [invoices]);


    // Advanced Filter state
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [filterCriteria, setFilterCriteria] = useState({ logic: 'AND', rows: [] });
    const [advancedFilters, setAdvancedFilters] = useState(null);

    const advanceFilterFields = [
        { label: 'Invoice Number', value: 'InvoiceNumber' },
        { label: 'Customer Name', value: 'ContactName' },
        { label: 'Status', value: 'Status' },
        { label: 'Amount', value: 'TotalAmount' },
        { label: 'Invoice Date', value: 'InvoiceDate', type: 'date' },
        { label: 'Valid Till Date', value: 'DueAt', type: 'date' },
        { label: 'Lead No', value: 'LeadNo' }
    ];

    // Detail view state
    const [detailView, setDetailView] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [showFooter, setShowFooter] = useState(true);
    const [showHistory, setShowHistory] = useState(true);

    // Edit view state (shared with InvoiceFilterationAction)
    const [editView, setEditView] = useState(null);
    const [editForm, setEditForm] = useState({});

    // CRM Lead Action State
    const [leadAction, setLeadAction] = useState({
        open: false,
        mode: '',
        leadId: null,
        data: null
    });

    // Template Download Panel State
    const [isTemplatePanelOpen, setIsTemplatePanelOpen] = useState(false);
    const [selectedInvoiceForTemplate, setSelectedInvoiceForTemplate] = useState(null);

    const handleOpenTemplatePanel = (invoice) => {
        setSelectedInvoiceForTemplate(invoice);
        setIsTemplatePanelOpen(true);
    };

    // Currency symbol from API
    const [currencySymbol, setCurrencySymbol] = useState('₹');

    const [viewingLeadId, setViewingLeadId] = useState(null);

    // Ref to call action methods from the action component
    const actionRef = useRef(null);
    const dropdownRef = useRef(null); // Added for dropdown click outside
    const WCF_BASE = (process.env.REACT_APP_WCF_API_BASE_URL || '').replace(/\/$/, '');
    const NEWV3_BASE_URL = (process.env.REACT_APP_SERVICES_API_BASE_URL || '').replace(/\/$/, '');
    const GET_INVOICE_LIST = `${NEWV3_BASE_URL}${API_ENDPOINTS.INVOICE.GET_INVOICE_LIST_BYID}`.trim();
    const GET_INVOICE_DETAILS_URL = `${NEWV3_BASE_URL}/${API_ENDPOINTS.INVOICE.GET_INVOICE_DETAILS}`.trim();
    const SAVE_APPROVAL_URL = `${NEWV3_BASE_URL}${API_ENDPOINTS.INVOICE.SAVE_APPROVAL_SETTING}`.trim();
    const CANCEL_INVOICE_URL = `${NEWV3_BASE_URL}/${API_ENDPOINTS.INVOICE.CANCEL_INVOICE}`.trim();

    const GET_PRINT_LINK_URL = `${NEW_BASE_URL}${API_ENDPOINTS.INVOICE.PRINT_INVOICE}`;

    const Download_Invoice_Template = `${NEWV3_BASE_URL}/${API_ENDPOINTS.INVOICE.DOWNLOAD_INVOICE_TEMPLATE}`.trim();
    const Save_Approval_Data = `${NEWV3_BASE_URL}/${API_ENDPOINTS.INVOICE.SAVE_APPROVAL_ACTION}`.trim();
    const Get_Approved_Data = `${NEWV3_BASE_URL}/${API_ENDPOINTS.Quotation.GET_APPROVED_DATA}`.trim();
    const Get_Approved_Data_Entity = `${NEWV3_BASE_URL}/${API_ENDPOINTS.INVOICE.GET_ENTITY_APPROVAL_SETTING}`.trim();

    const handleApproveReject = async (status) => {
        let remark = approvalRemark;
        setDetailLoading(true);

        if (status === 'Rejected') {
            const { value: text, isConfirmed } = await Swal.fire({
                title: 'Reject Invoice',
                input: 'textarea',
                inputLabel: 'Please provide a reason for rejection',
                inputPlaceholder: 'Type your remark here...',
                inputAttributes: {
                    'aria-label': 'Type your remark here'
                },
                showCancelButton: true,
                confirmButtonText: 'Reject',
                confirmButtonColor: '#ef4444'
            });

            if (!isConfirmed) return;
            remark = text || "";
        }

        try {
            const userId = localStorage.getItem('userId') || "34594";
            const invoiceId = detailView?.InvoiceId || detailView?.ID || detailView?.Id ||
                detailView?._invoice?.InvoiceId || detailView?._invoice?.ID || detailView?._invoice?.Id;

            // Get ApprovalId from detailView or history
            const approvalId = detailView?.ApprovalId || (detailView?.invoiceHistories && detailView?.invoiceHistories[0]?.ApprovalId) || "0";

            const payload = {
                Token: "-2295521862261168",
                Details: {
                    UserId: parseInt(userId, 10),
                    ApprovalId: parseInt(approvalId, 10),
                    EntityId: parseInt(invoiceId, 10),
                    Remarks: remark,
                    status: status,
                    IsActive: true
                }
            };


            const response = await fetch(Save_Approval_Data, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const result = await response.json();
            const data = (result && typeof result === 'object' && 'd' in result) ? result.d : result;

            if (data === true || data === "true" || data === null || (data && data.Status === 1)) {
                if (status === 'Approved') {
                    try {
                        const approvedDataPayload = {
                            Token: "-2295521862261168",
                            Details: {
                                UserId: parseInt(userId, 10),
                                EntityName: 'invoice',
                                EntityId: Number(invoiceId)
                            }
                        };
                        await fetch(Get_Approved_Data, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify(approvedDataPayload)
                        });
                    } catch (apiErr) {
                        console.error("Get_Approved_Data API error:", apiErr);
                    }
                }

                // Check for next approver immediate
                try {
                    const appDetailResp = await fetch(Get_Approved_Data_Entity, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            UserId: userId,
                            EntityName: 'Invoice',
                            EntityId: Number(invoiceId)
                        })
                    });
                    const appDetailData = await appDetailResp.json();
                    const nextApproval = appDetailData?.d || appDetailData;

                    setDetailView(prev => ({
                        ...prev,
                        ApprovalId: nextApproval?.Id || 0,
                        ApprovalUser: nextApproval?.User,
                        ApprovalEmail: nextApproval?.Email
                    }));
                } catch (e) {
                    console.error("Error checking next approver:", e);
                }

                Swal.fire({
                    title: 'Success',
                    text: `Invoice ${status} successfully.`,
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false
                });
                setApprovalRemark('');
                handleShowInvoice(detailView?._invoice); // Refresh details
                fetchInvoices(); // Refresh list
            } else {
                throw new Error(data?.Message || 'Failed to update approval status');
            }
        } catch (error) {
            console.error("Approval Error:", error);
            Swal.fire('Error', 'Failed to update approval status.', 'error');
        } finally {
            setDetailLoading(false);
        }
    };

    const handlePrint = async (inv) => {

        try {
            const invoiceId = inv?.InvoiceId || inv?.ID || inv?.Id ||
                inv?._invoice?.InvoiceId || inv?._invoice?.ID || inv?._invoice?.Id ||
                detailView?._invoice?.InvoiceId || detailView?._invoice?.ID || detailView?._invoice?.Id;



            const payload = {
                Token: API_ENDPOINTS.TOKE.UNIVERSAL_TOKEN || "-2295521862261168",
                Details: {
                    QuotationId: 0,
                    InvoiceId: Number(invoiceId) || 0
                }
            };

            const response = await fetch(GET_PRINT_LINK_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            const result = await response.json();

            if (result && result.Status === 1 && result.Details) {
                const printWindow = window.open(result.Details, '_blank');
                if (printWindow) {
                    printWindow.focus();

                    // Try to trigger print dialog once loaded
                    let printTriggered = false;
                    const checkReadyInterval = setInterval(() => {
                        try {
                            if (printWindow.document && printWindow.document.readyState === 'complete') {
                                if (!printTriggered) {
                                    printTriggered = true;
                                    clearInterval(checkReadyInterval);
                                    printWindow.print();
                                }
                            }
                        } catch (e) {
                            // Likely cross-origin or pop-up blocker issues
                            clearInterval(checkReadyInterval);
                        }
                    }, 500);

                    // Safety timeout
                    setTimeout(() => clearInterval(checkReadyInterval), 10000);
                }
            } else {
                Swal.fire({
                    title: 'Service Unavailable',
                    text: result.Message || 'Could not generate print link at this time.',
                    icon: 'warning',
                    confirmButtonColor: '#3b82f6'
                });
            }
        } catch (error) {
            console.error("Print Error:", error);
            Swal.fire({
                title: 'Print Error',
                text: 'Failed to connect to print service.',
                icon: 'error',
                confirmButtonColor: '#3b82f6'
            });
        }
    };

    const getInitials = (name) => {
        if (!name || name === 'N/A' || name === 'null' || name === '-') return '?';
        const trimmed = name.trim();

        // Handle phone numbers starting with + (e.g. +98451 → "98")
        if (trimmed.startsWith('+')) {
            return trimmed.substring(1).replace(/\D/g, '').substring(0, 2);
        }

        // Handle pure phone numbers (digits only, e.g. 9032378 → "90")
        const digitsOnly = trimmed.replace(/\D/g, '');
        if (digitsOnly.length >= 4 && /^\+?\d[\d\s\-()]+$/.test(trimmed)) {
            return digitsOnly.substring(0, 2);
        }

        const parts = trimmed.split(/\s+/);
        // Single word: only the first letter (e.g. "Ajay" → "A")
        if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
        // Multi-word: first letter of first + first letter of last word (e.g. "Ajay Koul" → "AK")
        return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    };

    const getAvatarStyle = (name) => {
        const colors = [
            { bg: '#e0e7ff', text: '#4338ca' }, // indigo
            { bg: '#dcfce7', text: '#15803d' }, // green
            { bg: '#fef9c3', text: '#a16207' }, // yellow
            { bg: '#fee2e2', text: '#b91c1c' }, // red
            { bg: '#ffedd5', text: '#9a3412' }, // orange
            { bg: '#f3e8ff', text: '#7e22ce' }, // purple
            { bg: '#dbeafe', text: '#1d4ed8' }, // blue
            { bg: '#fae8ff', text: '#a21caf' }, // fuchsia
            { bg: '#e0f2fe', text: '#0369a1' }, // sky
            { bg: '#ccfbf1', text: '#0f766e' }, // teal
        ];
        if (!name || name === '-') return colors[0];
        const index = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length;
        return colors[index];
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setActiveDropdownId(null);
            }
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    const getToken = () => {
        try {
            return API_ENDPOINTS.TOKEN?.UNIVERSAL_TOKEN || API_ENDPOINTS.TOKE?.UNIVERSAL_TOKEN || localStorage.getItem('Token') || '';
        } catch (e) {
            return localStorage.getItem('Token') || '';
        }
    };

    const safeStr = (v) => (v === null || v === undefined) ? '' : String(v).trim();

    const parseResponse = async (resp) => {
        try {
            const text = await resp.text();
            if (!text) return null;
            const match = text.match(/<string[^>]*>(.*?)<\/string>/s);
            const jsonStr = match ? match[1] : text;
            return JSON.parse(jsonStr);
        } catch (e) {
            console.error('Error parsing response:', e);
            return null;
        }
    };

    // Search Debouncing
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    const fetchInvoices = async () => {
        setLoading(true);
        setHasFetched(false);
        try {
            const userId = localStorage.getItem('userId') || '34594';

            // Construct FilterText from advanced filter criteria
            let advancedConditions = [];
            if (filterCriteria.rows && filterCriteria.rows.length > 0) {
                advancedConditions = filterCriteria.rows.map(row => {
                    let condition = '';
                    const field = row.field;
                    const value = row.value;
                    const op = row.operator;

                    switch (op) {
                        case 'contains': condition = `${field} LIKE '%${value}%'`; break;
                        case 'does_not_contain': condition = `${field} NOT LIKE '%${value}%'`; break;
                        case 'begins_with': condition = `${field} LIKE '${value}%'`; break;
                        case 'ends_with': condition = `${field} LIKE '%${value}'`; break;
                        case 'does_not_begin_with': condition = `${field} NOT LIKE '${value}%'`; break;
                        case 'does_not_end_with': condition = `${field} NOT LIKE '%${value}'`; break;
                        case 'equal_to': condition = `${field} = '${value}'`; break;
                        case 'not_equal_to': condition = `${field} <> '${value}'`; break;
                        default: condition = `${field} LIKE '%${value}%'`;
                    }
                    return condition;
                }).filter(Boolean);
            }

            let finalFilterText = "";
            if (advancedConditions.length > 0) {
                finalFilterText = `(${advancedConditions.join(` ${filterCriteria.logic} `)})`;
            }

            const payload = {
                Token: "-2295521862261168",
                Details: {
                    UserId: parseInt(userId, 10),
                    FullText: "",
                    FilterText: finalFilterText,
                    OperationMode: "",
                    Status: "",
                    OffsetValue: 0,
                    PagingSize: 5000
                }
            };

            const response = await fetch(GET_INVOICE_LIST, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                const parsed = await response.json();
                const data = parsed?.Details || parsed?.d || parsed?.Data || parsed?.data || parsed;
                const list = Array.isArray(data) ? data : [];
                setInvoices(list);
                setTotalRecords(list.length);

                if (list.length > 0 && list[0].CurrencySymbol) {
                    setCurrencySymbol(list[0].CurrencySymbol);
                } else if (parsed.CurrencySymbol) {
                    setCurrencySymbol(parsed.CurrencySymbol);
                }
            }
        } catch (error) {
            console.error('Error fetching invoices:', error);
        } finally {
            setLoading(false);
            setHasFetched(true);
            setIsInitialLoad(false);
        }
    };

    useEffect(() => {
        setLoading(true);
        fetchInvoices();
    }, [filterCriteria, advancedFilters]);

    useEffect(() => {
        setCurrentPage(1);
    }, [statusFilters, advancedFilters]);


    // Filtered invoices for display if local status filter or advanced filter is active
    const filteredInvoices = useMemo(() => {
        let result = invoices;

        // 1. Status Filter
        if (statusFilters.length > 0) {
            result = result.filter(inv => statusFilters.includes(getStandardStatus(inv.Status)));
        }

        // 2. Advanced Filter from SubHeader
        if (advancedFilters && advancedFilters.rows && advancedFilters.rows.length > 0) {
            result = result.filter(inv => {
                const results = advancedFilters.rows.map(row => {
                    const fieldVal = inv[row.field];
                    const val = String(fieldVal || '').toLowerCase();
                    const target = String(row.value || '').toLowerCase();

                    switch (row.operator) {
                        case 'equals': return val === target;
                        case 'not_equal': return val !== target;
                        case 'contains': return val.includes(target);
                        case 'not_contains': return !val.includes(target);
                        case 'starts_with': return val.startsWith(target);
                        case 'ends_with': return val.endsWith(target);
                        default: return true;
                    }
                });

                return advancedFilters.logic === 'ALL'
                    ? results.every(r => r)
                    : results.some(r => r);
            });
        }

        // 3. Client-side Search (Invoice Number & Customer Name)
        if (debouncedSearchTerm) {
            const lowerSearch = debouncedSearchTerm.toLowerCase();
            result = result.filter(inv => 
                String(inv.InvoiceNumber || '').toLowerCase().includes(lowerSearch) ||
                String(inv.ContactName || '').toLowerCase().includes(lowerSearch)
            );
        }

        return result;
    }, [invoices, statusFilters, advancedFilters, debouncedSearchTerm]);

    const allStatusStats = useMemo(() => {
        const currentStats = { total: filteredInvoices.length, approved: 0, draft: 0, pending: 0, cancelled: 0 };
        filteredInvoices.forEach(inv => {
            const status = getStandardStatus(inv.Status);
            if (status === 'Paid/Approved') currentStats.approved++;
            else if (status === 'Draft') currentStats.draft++;
            else if (status === 'Sent For Approval') currentStats.pending++;
            else if (status === 'Cancelled') currentStats.cancelled++;
        });

        return currentStats;
    }, [filteredInvoices]);

    const handleAdvanceFilterSubmit = (filterData) => {
        setAdvancedFilters(filterData);
    };

    const handleStatusFilter = (status) => {
        if (status === 'ResetAll') {
            setStatusFilters([]);
            setSearchTerm('');
            setAdvancedFilters(null);
            setFilterCriteria({ logic: 'AND', rows: [] });
            setCurrentPage(1);
            return;
        }

        if (status === 'All') {
            setStatusFilters([]);
        } else {
            setStatusFilters(prev => {
                if (prev.includes(status)) {
                    return prev.filter(s => s !== status);
                } else {
                    return [...prev, status];
                }
            });
        }
    };

    const paginatedInvoices = useMemo(() => {
        // Since the server-side might already be providing a page, 
        // but if we are doing local view of a larger fetch:
        const start = (currentPage - 1) * itemsPerPage;
        return filteredInvoices.slice(start, start + itemsPerPage);
    }, [filteredInvoices, currentPage, itemsPerPage]);

    const handlePageChange = (pageNumber) => {
        if (pageNumber >= 1) {
            setCurrentPage(pageNumber);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    // Transform invoices for Calendar View
    const calendarEvents = useMemo(() => {
        return invoices.map(inv => {
            // Robust date parsing for consistent calendar display
            let eventDate = inv.InvoiceDate;
            if (typeof eventDate === 'string' && eventDate.includes('/')) {
                // If it's DD/MM/YYYY, try to normalize it for new Date()
                const parts = eventDate.split('/');
                if (parts.length === 3) {
                    // Try to detect if it's DD/MM/YYYY (common in this app)
                    // If first part is > 12, it's definitely day
                    if (parseInt(parts[0]) > 12) {
                        eventDate = `${parts[1]}/${parts[0]}/${parts[2]}`; // MM/DD/YYYY
                    }
                }
            }

            return {
                id: inv.InvoiceId || inv.ID || inv.Id,
                title: `${inv.InvoiceNumber} - ${inv.ContactName || inv.CustomerName || inv.PersonName || inv.Name || '-'}`,
                date: eventDate,
                status: inv.Status?.toLowerCase().replace(/\s+/g, '-'),
                amount: inv.TotalAmount || inv.Amount,
                ...inv
            };
        });
    }, [invoices]);

    const handleShowInvoice = async (invoice) => {
        setActiveDropdownId(null);
        setDetailLoading(true);
        setDetailView({ _loading: true, _invoice: invoice });

        try {
            const userId = localStorage.getItem('userId') || '34594';
            const invoiceId = invoice.InvoiceId || invoice.ID || invoice.Id || 
                invoice._invoice?.InvoiceId || invoice._invoice?.ID || invoice._invoice?.Id;
            
            const payload = {
                Token: "-2295521862261168",
                Details: {
                    InvoiceId: Number(invoiceId),
                    TimeZone: 'India Standard Time'
                }
            };

            const response = await fetch(GET_INVOICE_DETAILS_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                const data = await response.json();
                const detail = data?.d || data?.Details || data?.Data || data?.data || data;
                
                let approvalDetail = null;
                let approvalHistory = [];

                // 1. Fetch Approval Detail (to get ApprovalId and Approver Name)
                if (invoice.HasApprovalSetting) {
                    try {
                       const appDetailResp = await fetch(Get_Approved_Data_Entity,
  {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                Token: "-2295521862261168",
                                Details: {
                                    UserId: parseInt(userId, 10),
                                    EntityName: 'Invoice',
                                    EntityId: Number(invoiceId)
                                }
                            })
                        });
                        const appDetailData = await appDetailResp.json();
                        approvalDetail = appDetailData?.d || appDetailData;
                    } catch (e) {
                        console.error("Error fetching approval detail:", e);
                    }

                    // 2. Fetch Approval History
                    try {
                        const appHistoryResp = await fetch(Get_Approved_Data, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                Token: "-2295521862261168",
                                Details: {
                                    UserId: parseInt(userId, 10),
                                    EntityName: 'invoice',
                                    EntityId: Number(invoiceId)
                                }
                            })
                        });
                        const appHistoryData = await appHistoryResp.json();
                        approvalHistory = appHistoryData?.d || appHistoryData || [];
                    } catch (e) {
                        console.error("Error fetching approval history:", e);
                    }
                }

                setDetailView({ 
                    ...detail, 
                    _invoice: invoice,
                    ApprovalId: approvalDetail?.Id,
                    ApprovalUser: approvalDetail?.User,
                    ApprovalEmail: approvalDetail?.Email,
                    invoiceHistories: approvalHistory.length > 0 ? approvalHistory : (detail?.invoiceHistories || [])
                });
            } else {
                setDetailView({ _invoice: invoice, _fallback: true });
            }
        } catch (error) {
            console.error('Error fetching invoice details:', error);
            setDetailView({ _invoice: invoice, _fallback: true });
        } finally {
            setDetailLoading(false);
        }
    };

    const handleRequestApproval = async (invoice) => {
        setLoading(true);
        try {
            const userId = localStorage.getItem('userId') || '34594';
            const payload = {
                Token: "-2295521862261168",
                Details: {
                    UserId: parseInt(userId, 10),
                    EntityName: 'Invoice',
                    EntityId: parseInt(invoice.InvoiceId || invoice.ID || invoice.Id, 10)
                }
            };

            const response = await fetch(SAVE_APPROVAL_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const result = await response.json();
            if (result && result.d === true) {
                Swal.fire({
                    title: 'Success',
                    text: 'Sent for approval successfully',
                    icon: 'success',
                    confirmButtonText: 'OK',
                    confirmButtonColor: '#28a745'
                });
                fetchInvoices();
            }
        } catch (error) {
            console.error('Error requesting approval:', error);
            Swal.fire('Error', 'Failed to send for approval', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateInvoiceStatus = async (invoice, mode) => {
        setActiveDropdownId(null);
        setLoading(true);
        try {
            const userId = localStorage.getItem('userId') || '34594';
            const invoiceId = invoice.InvoiceId || invoice.ID || invoice.Id;

            const payload = {
                Token: "-2295521862261168",
                Details: {
                    InvoiceIds: String(invoiceId),
                    Mode: mode,
                    UserId: parseInt(userId, 10)
                }
            };

            const response = await fetch(CANCEL_INVOICE_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const result = await response.json();
            if (result && (result.d === true || result.Success === true || result.Message?.toLowerCase().includes('success'))) {
                Swal.fire({
                    title: 'Success',
                    text: mode === 'deleted' ? 'Invoice deleted successfully.' : 'Invoice marked as cancelled.',
                    icon: 'success',
                    confirmButtonText: 'OK',
                    confirmButtonColor: '#28a745'
                });
                fetchInvoices();
            } else {
                Swal.fire('Error', result.d || `Failed to ${mode} invoice`, 'error');
            }
        } catch (error) {
            console.error(`Error ${mode} invoice:`, error);
            Swal.fire('Error', `Failed to ${mode} invoice`, 'error');
        } finally {
            setLoading(false);
        }
    };


    const handleExportToExcel = () => {
        if (!filteredInvoices || filteredInvoices.length === 0) {
            Swal.fire({
                title: 'No Data',
                text: 'There are no invoices to export.',
                icon: 'info',
                confirmButtonColor: '#3b82f6'
            });
            return;
        }

        try {
            // Prepare data for Excel with correct mappings based on column definitions
            const exportData = filteredInvoices.map((inv, index) => ({
                'SNo.': index + 1,
                'Invoice Number': inv.InvoiceNumber || inv.Number || '-',
                'Status': inv.Status || 'Draft',
                'Invoice Date': formatDate(inv.InvoiceDate || inv.InvoicedAt || inv.Date),
                'Valid Till': formatDate(inv.DueAt || inv.ExpiryDate || inv.ValidTillDate),
                'Customer Name': inv.ContactName || inv.CustomerName || inv.PersonName || 'NA',
                'Customer Email': inv.ContactEmail || inv.Email || '-',
                'Customer Phone': inv.ContactPhone || inv.MobileNo || inv.Mobile || '-',
                'Amount': inv.TotalAmount || inv.Amount || 0,
                'Lead No': inv.LeadNo || inv.LeadId || '-',
                'Created On': formatDate(inv.CreatedOn || inv.CreatedAt),
                'Created By': inv.AgentName || inv.CreatedBy || '-'
            }));

            // Create worksheet
            const ws = XLSX.utils.json_to_sheet(exportData);

            // Set column widths
            const colWidths = [
                { wch: 6 },  // SNo
                { wch: 20 }, // Invoice Number
                { wch: 15 }, // Status
                { wch: 15 }, // Invoice Date
                { wch: 15 }, // Valid Till
                { wch: 25 }, // Customer Name
                { wch: 30 }, // Customer Email
                { wch: 15 }, // Customer Phone
                { wch: 12 }, // Amount
                { wch: 10 }, // Lead No
                { wch: 15 }, // Created On
                { wch: 20 }  // Created By
            ];
            ws['!cols'] = colWidths;

            // Create workbook and append sheet
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Invoices');

            // Save file
            XLSX.writeFile(wb, `Invoices_Export_${new Date().toISOString().split('T')[0]}.xlsx`);

            Swal.fire({
                title: 'Export Successful',
                text: 'Invoice list has been downloaded as Excel.',
                icon: 'success',
                confirmButtonColor: '#3b82f6',
                timer: 2000
            });
        } catch (error) {
            console.error("Export error:", error);
            Swal.fire({
                title: 'Export Failed',
                text: 'An error occurred while generating the Excel file.',
                icon: 'error',
                confirmButtonColor: '#3b82f6'
            });
        }
    };

    const handleEditInvoice = (invoice) => {
        setActiveDropdownId(null);
        if (actionRef.current) {
            actionRef.current.handleEditInvoice(invoice);
        }
    };

    const handleDuplicateInvoice = (invoice) => {
        setActiveDropdownId(null);
        if (actionRef.current) {
            actionRef.current.handleDuplicateInvoice(invoice);
        }
    };

    const handleAddInvoice = () => {
        setActiveDropdownId(null);
        if (actionRef.current) {
            actionRef.current.handleAddInvoice();
        }
    };

    const handleApplyFilter = (criteria) => {
        setFilterCriteria(criteria);
    };

    const handleClearFilter = () => {
        setFilterCriteria({ logic: 'AND', rows: [] });
    };

    const getInitialsLocal = (name) => {
        if (!name || name === '-') return 'U';
        const trimmed = name.trim();
        if (trimmed.startsWith('+')) return trimmed.substring(1).replace(/\D/g, '').substring(0, 2) || 'U';
        const digitsOnly = trimmed.replace(/\D/g, '');
        if (digitsOnly.length >= 4 && /^\+?\d[\d\s\-()]+$/.test(trimmed)) return digitsOnly.substring(0, 2);
        const parts = trimmed.split(/\s+/);
        if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
        return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    };

    const formatCurrency = (amount) => {
        const val = parseFloat(amount) || 0;
        const formatted = new Intl.NumberFormat('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(val);
        return `${currencySymbol} ${formatted}`;
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        try {
            const date = new Date(dateStr);
            if (isNaN(date.getTime())) return dateStr;
            const day = String(date.getDate()).padStart(2, '0');
            const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            const month = monthNames[date.getMonth()];
            const year = date.getFullYear();
            return `${day}-${month}-${year}`;
        } catch { return dateStr; }
    };

    const invoiceCustomCards = [
        { title: allStatusStats.total ?? totalRecords ?? '-', subtitle: 'Total Invoices', icon: 'ri:list-check', color: 'primary', key: 'All' },
        {
            title: (statusFilters.length === 0 || statusFilters.includes('Paid/Approved')) ? (allStatusStats.approved ?? '-') : 0,
            subtitle: 'Paid/Approved', icon: 'ri:checkbox-circle-line', color: 'success', key: 'Paid/Approved'
        },
        {
            title: (statusFilters.length === 0 || statusFilters.includes('Draft')) ? (allStatusStats.draft ?? '-') : 0,
            subtitle: 'Draft', icon: 'ri:edit-line', color: 'info', key: 'Draft'
        },
        {
            title: (statusFilters.length === 0 || statusFilters.includes('Sent For Approval')) ? (allStatusStats.pending ?? '-') : 0,
            subtitle: 'Sent for Approval', icon: 'ri:time-line', color: 'warning', key: 'Sent For Approval'
        },
        {
            title: (statusFilters.length === 0 || statusFilters.includes('Cancelled')) ? (allStatusStats.cancelled ?? '-') : 0,
            subtitle: 'Expired/Cancelled', icon: 'ri:close-circle-line', color: 'error', key: 'Cancelled'
        }
    ];

    const columns = useMemo(() => [
        {
            id: 'Number',
            header: 'INVOICE NUMBER',
            accessorKey: 'InvoiceNumber',
            cell: info => {
                const inv = info.row.original;
                return (
                    <div className="quotation-number-cell"
                        onClick={() => handleShowInvoice(inv)}
                        style={{ cursor: 'pointer' }}>
                        <div className="quotation-icon-circle">
                            <FileText size={14} />
                        </div>
                        <span className="invoice-customer-name-link-premium" style={{ fontSize: '13px' }}>
                            {inv.InvoiceNumber || inv.Number || '-'}
                        </span>
                    </div>
                )
            }
        },
        {
            id: 'Customer',
            header: 'Customer',
            accessorKey: 'ContactName',
            cell: info => {
                const inv = info.row.original;
                const name = inv.ContactName || inv.CustomerName || inv.PersonName || 'NA';
                const initials = getInitials(name);
                const avatarStyle = getAvatarStyle(name);

                return (
                    <div className="invoice-customer-col-content-premium">
                        <ScoreCard
                            leadId={inv.LeadId || inv.LeadNo}
                            mobileNo={inv.ContactPhone || inv.MobileNo}
                            personName={name}
                            initialScore={inv.FollowupCount || inv.CurrentScore || 0}
                        >
                            <Avatar sx={{
                                width: 34, height: 34,
                                bgcolor: avatarStyle.bg, color: avatarStyle.text,
                                fontSize: '13px', fontWeight: 700,
                                border: 'none',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                            }}>
                                {initials}
                            </Avatar>
                        </ScoreCard>
                        <div className="invoice-customer-details-meta-premium" style={{ marginLeft: '4px' }}>
                            <div className="invoice-customer-name-group-modern">
                                <span className="invoice-customer-name-link-premium"
                                    style={{ fontSize: '14px' }}
                                    onClick={(e) => { e.stopPropagation(); setViewingLeadId(inv.LeadId || inv.LeadNo); }}>
                                    {name}
                                </span>
                            </div>
                            <Typography variant="caption" sx={{ color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px' }}>
                                {(inv.LeadNo || inv.LeadId) && (
                                    <>
                                        Lead No. {inv.LeadNo || inv.LeadId}
                                    </>
                                )}
                            </Typography>
                        </div>
                    </div>
                )
            }
        },
        {
            id: 'Mobile',
            header: 'Mobile Number',
            accessorKey: 'ContactPhone',
            cell: info => {
                const inv = info.row.original;
                return <span style={{ color: '#475569', fontWeight: 500 }}>{inv.ContactPhone || inv.MobileNo || '-'}</span>
            }
        },
        {
            id: 'Amount',
            header: 'Amount',
            accessorKey: 'TotalAmount',
            cell: info => <span style={{ fontWeight: 700, color: '#1e293b' }}>{formatCurrency(info.row.original.TotalAmount || info.row.original.Amount)}</span>
        },
        {
            id: 'InvoiceDate',
            header: 'Invoice Date',
            accessorKey: 'InvoiceDate',
            cell: info => formatDate(info.row.original.InvoiceDate)
        },
        {
            id: 'ValidTillDate',
            header: 'Valid Till Date',
            accessorKey: 'DueAt',
            cell: info => formatDate(info.row.original.DueAt || info.row.original.ExpiryDate || info.row.original.ValidTillDate)
        },
        {
            id: 'Status',
            header: 'Status',
            accessorKey: 'Status',
            cell: info => {
                const rawVal = info.getValue();
                const cleanVal = (rawVal && String(rawVal).trim()) ? String(rawVal).trim() : 'Draft';
                const statusClass = cleanVal.toLowerCase().replace(/\s+/g, '-');
                return (
                    <span className={`invoice-status-pill-luxury ${statusClass}`}>
                        {cleanVal}
                    </span>
                )
            }
        },
        {
            id: 'action',
            header: 'Action',
            cell: info => (
                <div className="invoice-action-icons-group" style={{ justifyContent: 'center' }}>
                    <InvoiceRowActions
                        invoice={info.row.original}
                        crmOperationPerms={crmOperationPerms}
                        onEdit={handleEditInvoice}
                        onDuplicate={handleDuplicateInvoice}
                        onRequestApproval={handleRequestApproval}
                        onShow={handleShowInvoice}
                        onUpdateStatus={handleUpdateInvoiceStatus}
                        onDownloadTemplate={handleOpenTemplatePanel}
                    />
                </div>
            )
        }
    ], [invoices, statusFilters]);

    // ─── Invoice Detail Panel ────────────────────────────────────────────────
    const renderDetailPanel = () => {
        if (!detailView) return null;

        const inv = detailView._invoice || {};
        const d = detailView;

        const leadDetail = d.leadDetail || {};
        const customerName = d.ContactName || leadDetail.PersonName || inv.CustomerName || inv.PersonName || '-';
        const customerPhone = d.ContactPhone || leadDetail.MobileNo || inv.MobileNo || '-';
        const customerEmail = d.ContactEmail || leadDetail.EmailID || '-';
        const leadNo = d.LeadId || leadDetail.LeadNo || inv.LeadId || '-';
        const initials = getInitials(customerName);
        const avatarStyle = getAvatarStyle(customerName);

        const companyName = d.CompanyName || '-';
        const companyAddress = d.CompanyAddress || '-';
        const companyPhone = d.CompanyPhone || '-';
        const companyEmail = d.CompanyEmail || '-';
        const companyTax = d.CompanyTaxNumber || '-';
        const companyPan = d.CompanyPanNumber || '-';

        const invoiceNumber = d.InvoiceNumber || inv.InvoiceNumber || inv.Number || '-';
        const status = String(d.Status || inv.Status || 'Draft');
        const invoiceDate = d.InvoicedAt || inv.InvoiceDate || '-';
        const validTill = d.DueAt || inv.ExpiryDate || '-';
        const amount = d.Amount || inv.TotalAmount || inv.Amount || 0;
        const currencySymbolLocal = d.CurrencySymbol || currencySymbol;

        const billToName = d.BillToName || customerName;
        const billToTax = (d.ContactTaxNumber || '').trim();
        const billToPan = (d.ContactPanNumber || '').trim();
        const billToEmail = d.BillToEmailId || d.ContactEmail || customerEmail;
        const billToAddress = [d.BillToAddress, d.BillToCity, d.BillToState, d.BillToCountry, d.BillToPincode]
            .map(v => (v || '').trim()).filter(Boolean).join(', ') || (d.ContactAddress || '').trim();

        const shipToName = d.ShipToName || customerName;
        const shipToEmail = d.ShipToEmailId || d.ContactEmail || '';
        const shipToAddress = [d.ShipToAddress, d.ShipToCity, d.ShipToState, d.ShipToCountry, d.ShipToPincode]
            .map(v => (v || '').trim()).filter(Boolean).join(', ') || '';

        const lineItems = d.invoiceItems || [];
        const allTaxRows = [];
        let subTotal = 0;
        lineItems.forEach(item => {
            subTotal += parseFloat(item.Total || item.Price * item.Quantity || 0);
            (item.invoiceItemTaxes || []).forEach(t => {
                allTaxRows.push({ name: t.Name, amount: t.Amount, rate: t.Rate });
            });
        });
        const grandTotal = parseFloat(amount) || subTotal + allTaxRows.reduce((s, t) => s + parseFloat(t.amount || 0), 0);

        const histories = d.invoiceHistories || [];
        const termsHtml = d.CompanyTermCondition || d.TermsCondition || '';
        const notesHtml = d.Notes || d.InvoiceNotes || '';
        const footerHtml = d.Footer || '';
        const stripHtml = (html) => html.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim();

        return (
            <div className="invoice-detail-overlay" onClick={() => setDetailView(null)}>
                <div className="invoice-detail-panel" onClick={(e) => e.stopPropagation()}>

                    <div className="detail-back-bar">
                        <div className="detail-back-bar-left" onClick={() => setDetailView(null)}>
                            <ArrowLeft size={18} />
                            <span>Back to Invoice</span>
                        </div>
                        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
                            <InvoiceOperations
                                invoice={d}
                                crmOperationPerms={crmOperationPerms}
                                onEmail={() => setLeadAction({ open: true, mode: 'mail', leadId: leadNo, data: d })}
                                onSms={() => setLeadAction({ open: true, mode: 'sms', leadId: leadNo, data: d })}
                                onWhatsApp={() => setLeadAction({ open: true, mode: 'whatsapp', leadId: leadNo, data: d })}
                                onPrint={() => handlePrint(d)}
                                onSuccess={() => fetchInvoices()}
                                onDownloadTemplate={handleOpenTemplatePanel}
                            />
                        </div>
                    </div>

                    {detailLoading ? (
                        <div style={{ padding: '60px', display: 'flex', justifyContent: 'center' }}>
                            <Loader message="Fetching details..." />
                        </div>
                    ) : (
                        <div className="detail-content">
                            {/* Approval Panel - Premium UI */}
                            {getStandardStatus(status) === 'Sent For Approval' && Number(d.ApprovalId || 0) > 0 && (
                                <div className="invoice-approval-header-panel">
                                    <div className="approval-panel-left">
                                        <div className="approval-status-icon-wrapper">
                                            <div className="approval-icon-bg">
                                                <img 
                                                    src="/waitingApproval.png" 
                                                    alt="Waiting Approval" 
                                                    style={{ width: '40px', height: '40px', objectFit: 'contain' }} 
                                                />
                                            </div>
                                        </div>
                                        <div className="approval-text-content">
                                            <h3 className="approval-title-modern">Approve the Invoice</h3>
                                            <p className="approval-subtitle-modern">This invoice has been submitted for approval. Verify and approve the invoice.</p>
                                        </div>
                                        <div className="approval-actions-modern">
                                            <button className="btn-approve-modern" onClick={() => handleApproveReject('Approved')}>Approve</button>
                                            <button className="btn-reject-modern" onClick={() => handleApproveReject('Rejected')}>Reject</button>
                                        </div>
                                    </div>

                                    <div className="approval-panel-right">
                                        <div className="approvers-section-modern">
                                            <span className="approvers-label">Approvers</span>
                                            <div className="approver-info-card">
                                                <Avatar sx={{
                                                    width: 32, height: 32,
                                                    bgcolor: '#fff', color: '#64748b',
                                                    fontSize: '12px', fontWeight: 700,
                                                    border: '1.5px solid #e2e8f0'
                                                }}>
                                                    <User size={18} />
                                                </Avatar>
                                                <span className="approver-name">{d.ApprovalUser || 'Approver'}</span>
                                            </div>
                                        </div>
                                        <div className="approval-history-link-wrapper" onClick={() => setShowHistory(true)}>
                                            <span className="approval-history-link-text">Approval History</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Customer Header - Modernized */}
                            <div className="detail-customer-header">
                                <div className="detail-customer-left">
                                    <div className="detail-avatar-container">
                                        <Avatar
                                            sx={{
                                                width: 56,
                                                height: 56,
                                                bgcolor: avatarStyle.bg,
                                                color: avatarStyle.text,
                                                fontSize: '20px',
                                                fontWeight: 800,
                                                borderRadius: '50% !important',
                                                border: `2px solid #fff`,
                                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
                                            }}
                                        >
                                            {initials}
                                        </Avatar>
                                    </div>
                                    <div>
                                        <div className="detail-customer-name">{customerName}</div>
                                        <div className="detail-customer-meta">
                                            <span>{customerPhone}</span>
                                            {leadNo !== '-' && <span className="detail-invoice-ref" style={{ margin: 0, padding: '2px 8px', fontSize: '11px' }}>Lead No: {leadNo}</span>}
                                        </div>
                                    </div>
                                </div>
                                <div className="detail-invoice-ref">
                                    Invoice: {invoiceNumber}
                                </div>
                            </div>

                            {/* Status Info Bar - Modernized */}
                            <div className="detail-info-bar">
                                <div className="detail-info-col">
                                    <span className="detail-info-label">Status</span>
                                    <span className={`status-badge ${status.toLowerCase().replace(/\s+/g, '-')}`}>
                                        {status}
                                    </span>
                                </div>
                                <div className="detail-info-col">
                                    <span className="detail-info-label">Customer</span>
                                    <span className="detail-info-value">{customerName}</span>
                                </div>
                                <div className="detail-info-col">
                                    <span className="detail-info-label">Amount</span>
                                    <span className="detail-info-value" style={{ color: '#0f172a', fontWeight: 800 }}>{currencySymbolLocal} {parseFloat(amount || 0).toLocaleString()}</span>
                                </div>
                                <div className="detail-info-col">
                                    <span className="detail-info-label">Valid Till</span>
                                    <span className="detail-info-value">{validTill}</span>
                                </div>
                            </div>

                            {/* Company Details */}
                            <div className="detail-section">
                                <div className="detail-section-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span>Invoice Summary</span>
                                    <span style={{ fontSize: '13px', color: '#64748b' }}>Date: {invoiceDate}</span>
                                </div>
                                <div className="detail-grid-full">
                                    <div className="detail-field-row">
                                        <span className="field-label-fixed">Name:</span>
                                        <span className="field-value-rich">{companyName}</span>
                                    </div>
                                    <div className="detail-field-row">
                                        <span className="field-label-fixed">Address:</span>
                                        <span className="field-value-rich">{companyAddress}</span>
                                    </div>
                                    <div className="detail-field-row">
                                        <span className="field-label-fixed">Phone:</span>
                                        <span className="field-value-rich">{companyPhone}</span>
                                    </div>
                                    <div className="detail-field-row">
                                        <span className="field-label-fixed">Email:</span>
                                        <span className="field-value-rich">{companyEmail}</span>
                                    </div>
                                    <div className="detail-field-row">
                                        <span className="field-label-fixed">Tax:</span>
                                        <span className="field-value-rich">{companyTax}</span>
                                    </div>
                                    <div className="detail-field-row">
                                        <span className="field-label-fixed">Pan Number:</span>
                                        <span className="field-value-rich">{(companyPan && companyPan !== '-') ? companyPan : (companyTax && companyTax.length >= 12 ? companyTax.substring(2, 12) : '-')}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Bill To / Ship To */}
                            <div className="detail-section">
                                <div className="detail-grid-2">
                                    <div>
                                        <div className="detail-section-title">Bill To</div>
                                        <div className="detail-address-block">
                                            <div className="detail-addr-name">{billToName}</div>
                                            {billToTax && billToTax.trim() && <div className="detail-addr-line">Tax: {billToTax}</div>}
                                            {billToEmail && <div className="detail-addr-line">{billToEmail}</div>}
                                            {billToAddress && <div className="detail-addr-line">{billToAddress}</div>}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="detail-section-title">Ship To</div>
                                        <div className="detail-address-block">
                                            <div className="detail-addr-name">{shipToName}</div>
                                            {shipToEmail && <div className="detail-addr-line">{shipToEmail}</div>}
                                            {shipToAddress && <div className="detail-addr-line">{shipToAddress}</div>}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Line Items Table */}
                            <div className="detail-section">
                                <div className="detail-section-title">Line Items</div>
                                <table className="detail-items-table">
                                    <thead>
                                        <tr>
                                            <th>PRODUCTS</th>
                                            <th>DESCRIPTION</th>
                                            <th style={{ textAlign: 'center' }}>VOLUME</th>
                                            <th style={{ textAlign: 'right' }}>PRICE</th>
                                            <th style={{ textAlign: 'right' }}>AMOUNT</th>
                                            <th>HSN/SAC CODE</th>
                                            <th>TAXES</th>
                                            <th style={{ textAlign: 'right' }}>TAXAMOUNT</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {lineItems.length > 0 ? lineItems.map((item, i) => {
                                            const itemTaxes = item.invoiceItemTaxes || [];
                                            return (
                                                <React.Fragment key={i}>
                                                    <tr>
                                                        <td style={{ fontWeight: 700, color: '#1e293b' }}>{item.Name || '-'}</td>
                                                        <td style={{ color: '#64748b' }}>{item.Description || ''}</td>
                                                        <td style={{ textAlign: 'center', fontWeight: 600 }}>{item.Quantity || 1}</td>
                                                        <td style={{ textAlign: 'right' }}>{currencySymbolLocal} {parseFloat(item.Price || 0).toLocaleString()}</td>
                                                        <td style={{ textAlign: 'right', fontWeight: 700 }}>{currencySymbolLocal} {parseFloat(item.Total || item.Price || 0).toLocaleString()}</td>
                                                        <td style={{ fontWeight: 500 }}>{item.ItemHsnSacCode || 'NO DATA'}</td>
                                                        <td style={{ color: '#64748b' }}>{item.ItemTax || '-'}</td>
                                                        <td style={{ textAlign: 'right', fontWeight: 500 }}>{item.ItemTaxAmount || '-'}</td>
                                                    </tr>
                                                    {itemTaxes.map((t, ti) => (
                                                        <tr key={`tax-${i}-${ti}`} className="tax-subrow">
                                                            <td colSpan="2"></td>
                                                            <td></td>
                                                            <td></td>
                                                            <td></td>
                                                            <td style={{ color: '#64748b' }}>{t.Name}</td>
                                                            <td style={{ color: '#64748b' }}>{t.Rate}</td>
                                                            <td style={{ textAlign: 'right', color: '#64748b' }}>{currencySymbolLocal} {parseFloat(t.Amount || 0).toLocaleString()}</td>
                                                        </tr>
                                                    ))}
                                                </React.Fragment>
                                            );
                                        }) : (
                                            <tr>
                                                <td colSpan="8" style={{ textAlign: 'center', color: '#94a3b8', padding: '40px' }}>No line items found</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>

                                {/* Totals */}
                                <div className="detail-totals">
                                    <div className="totals-row">
                                        <span style={{ fontWeight: 600 }}>SUB TOTAL</span>
                                        <span style={{ fontWeight: 700, color: '#1e293b' }}>{currencySymbolLocal} {subTotal.toLocaleString()}</span>
                                    </div>
                                    {allTaxRows.map((t, i) => (
                                        <div className="totals-row" key={i}>
                                            <span style={{ fontWeight: 600 }}>{t.name}</span>
                                            <span style={{ fontWeight: 700 }}>{currencySymbolLocal} {parseFloat(t.amount || 0).toLocaleString()}</span>
                                        </div>
                                    ))}
                                    <div className="totals-row grand-total">
                                        <span className="grand-total-label">GRAND TOTAL</span>
                                        <span className="grand-total-value">{currencySymbolLocal} {grandTotal.toLocaleString()}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Terms & Condition */}
                            {termsHtml && (
                                <div className="detail-section">
                                    <div className="detail-section-title">Terms & Condition</div>
                                    <div className="detail-rich-text" style={{ padding: '10px 0', color: '#64748b', fontSize: '13px', lineHeight: 1.6 }}>
                                        {stripHtml(termsHtml).split('\n').filter(Boolean).map((line, i) => (
                                            <div key={i}>{line}</div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Histories Section */}
                            <div className="detail-section">
                                <div className="detail-section-title" onClick={() => setShowHistory(!showHistory)} style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span>Approval History</span>
                                    {showHistory ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                </div>
                                {showHistory && (
                                    <div className="detail-history-container" style={{ marginTop: '16px' }}>
                                        <table className="detail-history-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                                            <thead>
                                                <tr>
                                                    <th style={{ textAlign: 'left', padding: '12px', background: '#f8fafc', fontSize: '11px', color: '#64748b', textTransform: 'uppercase' }}>Date</th>
                                                    <th style={{ textAlign: 'left', padding: '12px', background: '#f8fafc', fontSize: '11px', color: '#64748b', textTransform: 'uppercase' }}>Status</th>
                                                    <th style={{ textAlign: 'left', padding: '12px', background: '#f8fafc', fontSize: '11px', color: '#64748b', textTransform: 'uppercase' }}>Remark</th>
                                                    <th style={{ textAlign: 'left', padding: '12px', background: '#f8fafc', fontSize: '11px', color: '#64748b', textTransform: 'uppercase' }}>Action By</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {histories.length > 0 ? histories.map((h, i) => (
                                                    <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                        <td style={{ padding: '12px', fontSize: '13px' }}>{h.CreatedOn || h.ActionDate || '-'}</td>
                                                        <td style={{ padding: '12px', fontSize: '13px' }}>
                                                            <span className={`status-badge ${(h.Status || '').toLowerCase().replace(/\s+/g, '-')}`} style={{ fontSize: '11px' }}>
                                                                {h.Status || '-'}
                                                            </span>
                                                        </td>
                                                        <td style={{ padding: '12px', fontSize: '13px', color: '#64748b' }}>{h.Discription || h.Description || '-'}</td>
                                                        <td style={{ padding: '12px', fontSize: '13px', fontWeight: 500 }}>{h.CreatedBy || h.ActionBy || '-'}</td>
                                                    </tr>
                                                )) : (
                                                    <tr>
                                                        <td colSpan="4" style={{ textAlign: 'center', padding: '24px', color: '#94a3b8' }}>No history found</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>

                        </div>
                    )}
                </div>
            </div>
        );
    };

    const invoiceStatusConfig = {
        'paid': '#10b981',
        'approved': '#10b981',
        'sent-for-approval': '#f59e0b',
        'sent': '#16a34a',
        'draft': '#3b82f6',
        'rejected': '#ef4444',
        'cancelled': '#ef4444',
        'deleted': '#ef4444'
    };

    const InvoiceCalendarLegend = () => (
        <Stack direction="row" spacing={2} sx={{ mt: 1, flexWrap: 'wrap', gap: '12px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#10b981' }} />
                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#475569' }}>Paid/Approved</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#f59e0b' }} />
                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#475569' }}>Sent For Approval</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#3b82f6' }} />
                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#475569' }}>Draft</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#ef4444' }} />
                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#475569' }}>Cancelled/Expired</Typography>
            </Box>
        </Stack>
    );

    const renderInvoiceCalendarEvent = (event, view) => {
        const color = invoiceStatusConfig[event.status] || '#6366f1';
        const customerName = event.ContactName || event.CustomerName || event.PersonName || 'N/A';
        const createdBy = event.AgentName || event.CreatedBy || '-';

        const bgColors = {
            'paid': '#f0fdf4',
            'approved': '#f0fdf4',
            'sent-for-approval': '#fffbeb',
            'draft': '#eff6ff',
            'cancelled': '#fef2f2'
        };
        const bgColor = bgColors[event.status] || '#f8fafc';

        return (
            <Box
                onClick={(e) => {
                    e.stopPropagation();
                    if (e.target.tagName !== 'P' && e.target.tagName !== 'SPAN' && e.target.tagName !== 'IMG') {
                        handleShowInvoice(event);
                    }
                }}
                sx={{
                    bgcolor: bgColor,
                    borderLeft: `4px solid ${color}`,
                    borderRadius: '8px',
                    p: '10px 12px',
                    mb: '8px',
                    cursor: 'default',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    width: '100%',
                    boxSizing: 'border-box',
                    overflow: 'hidden',
                    flexShrink: 0,
                    '&:hover': {
                        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                        transform: 'translateY(-1px)',
                    }
                }}
            >
                <Typography
                    onClick={(e) => { e.stopPropagation(); handleShowInvoice(event); }}
                    sx={{
                        fontSize: '13px', fontWeight: 800, color: '#2563eb',
                        cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden',
                        textOverflow: 'ellipsis', width: '100%',
                        textDecoration: 'underline',
                        '&:hover': { textDecoration: 'underline' }
                    }}
                >
                    {event.InvoiceNumber || event.Number || 'INV-XXX'}
                </Typography>

                <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%', overflow: 'hidden' }}>
                    {/* Customer Info */}
                    <Box
                        onClick={(e) => { e.stopPropagation(); setViewingLeadId(event.LeadId); }}
                        sx={{
                            display: 'flex', alignItems: 'center', gap: '6px',
                            minWidth: 0, flexShrink: 1, cursor: 'pointer',
                            '&:hover p': { textDecoration: 'underline' }
                        }}
                    >
                        <Avatar sx={{
                            width: 20, height: 20, fontSize: '9px', fontWeight: 800,
                            bgcolor: '#fff', color: '#64748b', border: '1px solid #e2e8f0',
                            flexShrink: 0
                        }}>
                            {getInitials(customerName)}
                        </Avatar>
                        <Typography sx={{
                            fontSize: '11px', fontWeight: 600, color: '#2563eb',
                            textDecoration: 'underline',
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                            minWidth: 0
                        }}>
                            {customerName}
                        </Typography>
                    </Box>

                    {/* Creator Info */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0, flexShrink: 1 }}>
                        <Box sx={{
                            width: 20, height: 20, borderRadius: '50%', border: '1px solid #e2e8f0',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#fff',
                            flexShrink: 0
                        }}>
                            <Icon icon="ri:user-3-line" fontSize={10} color="#94a3b8" />
                        </Box>
                        <Typography sx={{
                            fontSize: '11px', fontWeight: 600, color: '#1e293b',
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                            minWidth: 0
                        }}>
                            {createdBy}
                        </Typography>
                    </Box>
                </Stack>
            </Box>
        );
    };

    // ─── Main Render ─────────────────────────────────────────────────────────
    return (
        <div className="invoice-module-container">

            {/* Detail View Overlay */}
            {detailView && renderDetailPanel()}

            {/* Edit View + Modals (InvoiceFilterationAction) */}
            <InvoiceFilterationAction
                ref={actionRef}
                editView={editView}
                setEditView={setEditView}
                editForm={editForm}
                setEditForm={setEditForm}
                fetchInvoices={fetchInvoices}
                getToken={getToken}
                crmOperationPerms={crmOperationPerms}
            />

            <InvoiceAdvancedFilter
                isOpen={isFilterOpen}
                onClose={() => setIsFilterOpen(false)}
                onApply={handleApplyFilter}
                onClear={handleClearFilter}
            />

            {!detailView && !editView && (
                <>
                    <SubHeader
                        pageName="Invoices"
                        pageCode="PAG10223"
                        title={searchTerm ? `Invoice List ( ${filteredInvoices.length} matching records )` : "Invoice List"}
                        searchQuery={searchTerm}
                        setSearchQuery={setSearchTerm}
                        viewMode={viewMode}
                        setViewMode={setViewMode}
                        onAddClick={crmOperationPerms?.Add ? handleAddInvoice : null}
                        showSearch={!!crmOperationPerms?.Search}
                        showViewSwitcher={true}
                        showGrid={false}
                        showTable={true}
                        showCalendar={true}
                        showSettings={true}
                        advanceFilterFields={advanceFilterFields}
                        onAdvanceFilterSubmit={crmOperationPerms?.Search ? handleAdvanceFilterSubmit : null}
                        showAdvanceFilter={!!crmOperationPerms?.Search}
                        customPadding="0 20px"
                        searchPlaceholder="Search invoices..."
                        customMenuItems={
                            <>
                                {crmOperationPerms?.Export !== false && (
                                    <button
                                        className="menu-action-btn"
                                        onClick={handleExportToExcel}
                                        title="Export"
                                    >
                                        <Upload size={20} />
                                    </button>
                                )}
                            </>
                        }
                    />

                    {viewMode !== 'calendar' && (
                        <div style={{ position: 'relative', zIndex: 1000, overflow: 'visible' }}>
                            <PremiumModuleCards
                                stats={allStatusStats}
                                onStatusClick={handleStatusFilter}
                                moduleName="Invoice"
                                customCards={invoiceCustomCards}
                                activeFilters={statusFilters.length > 0 ? statusFilters : ['All']}
                            />
                        </div>
                    )}



                    {viewMode === 'table' || viewMode === 'grid' ? (
                        <div className="invoice-table-container" style={{ position: 'relative' }}>
                            {loading && (
                                <div style={{
                                    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                                    backgroundColor: '#ffffff',
                                    zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center'
                                }}>
                                    <Loader message="Loading invoices..." />
                                </div>
                            )}
                            <PremiumModuleTable
                                data={paginatedInvoices}
                                columns={columns}
                                totalRecords={(debouncedSearchTerm || statusFilters.length > 0 || advancedFilters) ? filteredInvoices.length : totalRecords}
                                currentPage={currentPage}
                                itemsPerPage={itemsPerPage}
                                onPageChange={handlePageChange}
                                loading={loading || isInitialLoad}
                                hasFetched={hasFetched && !isInitialLoad}
                                isInitialLoad={isInitialLoad}
                                globalFilter={searchTerm}
                            />
                        </div>
                    ) : (
                        <div className="invoice-calendar-view" style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                            <DynamicCalendar
                                events={calendarEvents}
                                onEventClick={handleShowInvoice}
                                isLoading={loading}
                                statusConfig={invoiceStatusConfig}
                                entityLabel="invoice"
                                headerLegend={<InvoiceCalendarLegend />}
                                eventRenderer={renderInvoiceCalendarEvent}
                            />
                        </div>
                    )}
                </>
            )}


            {/* Centralized Lead Details Overlay */}
            {viewingLeadId && (
                <div className="invoice-lead-details-overlay-premium" style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    zIndex: 10000, background: '#fff'
                }}>
                    <CommonLeadDetails
                        leadId={viewingLeadId}
                        onBack={() => setViewingLeadId(null)}
                        module="invoice"
                    />
                </div>
            )}

            {/* Premium CRM Operations Modal */}
            {leadAction.open && (
                <div className="lead-action-modal-overlay" style={{
                    position: 'fixed', inset: 0, zIndex: 11000,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: '20px'
                }} onClick={() => setLeadAction({ ...leadAction, open: false })}>
                    <div className="lead-action-modal-content" style={{
                        backgroundColor: '#fff',
                        width: 'calc(100% - 40px)', maxWidth: '900px',
                        height: '85vh',
                        borderRadius: '12px',
                        display: 'flex', flexDirection: 'column',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                        position: 'relative',
                        overflow: 'hidden'
                    }} onClick={e => e.stopPropagation()}>
                        <div style={{ flex: 1, overflowY: 'auto', position: 'relative' }}>
                            <LeadActionOperation
                                entity={{ leadId: leadAction.leadId }}
                                mode={leadAction.mode}
                                onBack={() => setLeadAction({ ...leadAction, open: false })}
                                onSuccess={() => {
                                    setLeadAction({ ...leadAction, open: false });
                                    fetchInvoices();
                                }}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Invoice Template Download Panel */}
            <InvoiceTemplatePanel
                isOpen={isTemplatePanelOpen}
                onClose={() => setIsTemplatePanelOpen(false)}
                invoice={selectedInvoiceForTemplate}
                getToken={getToken}
                API_BASE={API_BASE}
                NEW_BASE_URL={NEW_BASE_URL}
            />
        </div>
    );
};

// --- Operations Dropdown Component (Gear Menu) ---
const InvoiceOperations = ({ invoice, crmOperationPerms, onEmail, onSms, onWhatsApp, onPrint, onSuccess, onDownloadTemplate }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const getStandardStatusLocal = (rawStatus) => {
        const s = String(rawStatus || '').toLowerCase();
        if (s.includes('paid') || s.includes('approved')) return 'Paid/Approved';
        if (s.includes('draft') || s.includes('edit')) return 'Draft';
        if (s.includes('sent') || s.includes('pend')) return 'Sent For Approval';
        if (s.includes('canc') || s.includes('rej') || s.includes('deleted')) return 'Cancelled';
        return 'Draft'; // Default
    };

    const rawStatus = invoice?.Status || '';
    const standardStatus = getStandardStatusLocal(rawStatus).toLowerCase();
    const isRestricted = standardStatus === 'cancelled' || standardStatus === 'sent for approval';

    const handleMarkAsSent = async () => {
        const result = await Swal.fire({
            title: 'Mark as Sent',
            text: 'Are you sure you want to mark this invoice as sent?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3b82f6',
            cancelButtonText: 'Cancel',
            confirmButtonText: 'Yes, mark it!'
        });

        if (result.isConfirmed) {
            try {
                const invoiceId = invoice?.InvoiceId || invoice?.ID || invoice?.Id ||
                    invoice?._invoice?.InvoiceId || invoice?._invoice?.ID || invoice?._invoice?.Id;

                const token = localStorage.getItem('token') || "-2295521862261168";
                const userId = localStorage.getItem('userId') || "34594";

                const payload = {
                    Token: token,
                    Details: {
                        InvoiceIds: String(invoiceId),
                        Mode: "Paid",
                        UserId: parseInt(userId)
                    }
                };

                const response = await fetch(`${NEW_BASE_URL}${API_ENDPOINTS.INVOICE.MARK_AS_SENT_INVOICE}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });

                const data = await response.json();

                if (data && (data.Status === 1 || data.d === "1" || data === 1)) {
                    Swal.fire({
                        title: 'Success',
                        text: 'Invoice marked as sent successfully.',
                        icon: 'success',
                        timer: 2000,
                        showConfirmButton: false
                    });
                    if (onSuccess) onSuccess();
                    setIsOpen(false);
                } else {
                    throw new Error(data?.Message || 'Failed to update status');
                }
            } catch (error) {
                console.error("Mark as Sent Error:", error);
                Swal.fire('Error', 'Failed to mark invoice as sent.', 'error');
            }
        }
    };

    return (
        <div style={{ position: 'relative' }} ref={dropdownRef}>
            <button
                className="invoice-settings-btn-premium"
                onClick={() => setIsOpen(!isOpen)}
                title="Operations"
            >
                <Settings size={20} />
            </button>

            {isOpen && (
                <div className="operations-dropdown-v3" onClick={e => e.stopPropagation()}>
                    {!isRestricted ? (
                        <>
                            <div className="dropdown-section-v3">
                                <div className="dropdown-item-v3 main">
                                    <Share2 size={16} />
                                    <span>Share Invoice</span>
                                </div>
                                <div className="dropdown-sub-items-v3">
                                    <div className="dropdown-item-v3 sub" onClick={() => { onEmail(); setIsOpen(false); }}>
                                        <Mail size={14} />
                                        <span>Email</span>
                                    </div>
                                    <div className="dropdown-item-v3 sub" onClick={() => { onSms(); setIsOpen(false); }}>
                                        <MessageSquare size={14} />
                                        <span>SMS</span>
                                    </div>
                                    <div className="dropdown-item-v3 sub" onClick={() => { onWhatsApp(); setIsOpen(false); }}>
                                        <MessageCircle size={14} />
                                        <span>Whatsapp</span>
                                    </div>
                                </div>
                            </div>

                            <div className="dropdown-item-v3" onClick={handleMarkAsSent}>
                                <Send size={16} />
                                <span>Mark as Sent</span>
                            </div>

                            {crmOperationPerms?.Print !== false && (
                                <div className="dropdown-item-v3" onClick={() => { onPrint(); setIsOpen(false); }}>
                                    <Printer size={16} />
                                    <span>Print</span>
                                </div>
                            )}

                            {crmOperationPerms?.Export !== false && (
                                <div className="dropdown-item-v3" onClick={() => { onDownloadTemplate(invoice); setIsOpen(false); }}>
                                    <Download size={16} />
                                    <span>Download PDF</span>
                                </div>
                            )}
                        </>
                    ) : null}

                    <div className="dropdown-item-v3" onClick={() => {
                        const el = document.querySelector('.detail-history-table');
                        if (el) el.scrollIntoView({ behavior: 'smooth' });
                        setIsOpen(false);
                    }}>
                        <History size={16} />
                        <span>Approval History</span>
                    </div>
                </div>
            )}
        </div>
    );
};

// --- Premium Row Actions Component with MUI Menu ---
const InvoiceRowActions = ({ invoice, crmOperationPerms, onEdit, onDuplicate, onRequestApproval, onShow, onUpdateStatus, onDownloadTemplate }) => {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);

    const handleClick = (event) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleClose = (e) => {
        if (e) e.stopPropagation();
        setAnchorEl(null);
    };

    const status = String(invoice.Status || '').toLowerCase();
    const isLocked = ['sent-for-approval', 'sent for approval', 'approved', 'cancelled', 'deleted', 'paid'].includes(status);
    const canModify = !isLocked;

    if (crmOperationPerms && !crmOperationPerms.Edit && !crmOperationPerms.Delete && !crmOperationPerms.View) {
        return null;
    }

    return (
        <div onClick={(e) => e.stopPropagation()}>
            <IconButton
                aria-label="more"
                id="long-button"
                aria-controls={open ? 'long-menu' : undefined}
                aria-expanded={open ? 'true' : undefined}
                aria-haspopup="true"
                onClick={handleClick}
                size="small"
                sx={{
                    color: '#94a3b8',
                    transition: 'all 0.2s',
                    '&:hover': { bgcolor: '#f1f5f9', color: '#6366f1' }
                }}
            >
                <MoreVertical size={18} />
            </IconButton>
            <Menu
                id="long-menu"
                MenuListProps={{ 'aria-labelledby': 'long-button' }}
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                PaperProps={{
                    elevation: 0,
                    sx: {
                        overflow: 'visible',
                        filter: 'drop-shadow(0px 8px 24px rgba(148, 163, 184, 0.15))',
                        mt: 1,
                        borderRadius: '12px',
                        minWidth: '200px',
                        border: '1px solid #f1f5f9',
                        '& .MuiMenuItem-root': {
                            fontSize: '13px',
                            fontWeight: 500,
                            padding: '10px 16px',
                            gap: '4px',
                            color: '#475569',
                            transition: 'all 0.2s',
                            '&:hover': {
                                bgcolor: '#f8fafc',
                                color: '#1e293b'
                            }
                        },
                        '&:before': {
                            content: '""',
                            display: 'block',
                            position: 'absolute',
                            top: 0,
                            right: 14,
                            width: 10,
                            height: 10,
                            bgcolor: 'background.paper',
                            transform: 'translateY(-50%) rotate(45deg)',
                            zIndex: 0,
                            borderLeft: '1px solid #f1f5f9',
                            borderTop: '1px solid #f1f5f9',
                        },
                    },
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                {crmOperationPerms?.Edit !== false && canModify && status !== 'rejected' && status !== 'reject' && (
                    <MenuItem onClick={() => { handleClose(); onRequestApproval(invoice); }}>
                        <ListItemIcon><FileText size={16} color="#3b82f6" /></ListItemIcon>
                        <ListItemText>Request Approval</ListItemText>
                    </MenuItem>
                )}

                {crmOperationPerms?.View !== false && (
                <MenuItem onClick={() => { handleClose(); onShow(invoice); }}>
                    <ListItemIcon><Eye size={16} color="#06b6d4" /></ListItemIcon>
                    <ListItemText>Show</ListItemText>
                </MenuItem>
                )}

                {crmOperationPerms?.Edit !== false && canModify && (
                    <MenuItem onClick={() => { handleClose(); onEdit(invoice); }}>
                        <ListItemIcon><Edit3 size={16} color="#f59e0b" /></ListItemIcon>
                        <ListItemText>Edit</ListItemText>
                    </MenuItem>
                )}

                {crmOperationPerms?.Add !== false && (
                <MenuItem onClick={() => { handleClose(); onDuplicate(invoice); }}>
                    <ListItemIcon><Copy size={16} color="#8b5cf6" /></ListItemIcon>
                    <ListItemText>Duplicate</ListItemText>
                </MenuItem>
                )}



                {canModify && (
                    <>
                        <Divider sx={{ my: '4px !important', opacity: 0.6 }} />
                        {crmOperationPerms?.Edit !== false && (
                        <MenuItem onClick={() => { handleClose(); onUpdateStatus(invoice, 'cancelled'); }} sx={{ color: '#ef4444 !important' }}>
                            <ListItemIcon><X size={16} color="#ef4444" /></ListItemIcon>
                            <ListItemText>Cancel</ListItemText>
                        </MenuItem>
                        )}
                        {crmOperationPerms?.Delete !== false && (
                        <MenuItem onClick={() => { handleClose(); onUpdateStatus(invoice, 'deleted'); }} sx={{ color: '#ef4444 !important' }}>
                            <ListItemIcon><Trash2 size={16} color="#ef4444" /></ListItemIcon>
                            <ListItemText>Delete</ListItemText>
                        </MenuItem>
                        )}
                    </>
                )}
            </Menu>
        </div>
    );
};

export default InvoiceModule;
